
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { resetSessionState } from "@/services/auth/sessionUtils";
import { toast } from "sonner";

/**
 * Hook for managing reagent loading state with safety timeout
 */
export const useReagentLoadingState = (
  loadingReagents: boolean, 
  isFetching: boolean, 
  authLoading: boolean, 
  isRetrying: boolean,
  reagents: any[]
) => {
  const [forceLoadComplete, setForceLoadComplete] = useState(false);

  // Safety timeout for loading states
  useEffect(() => {
    if ((loadingReagents || isFetching) && !forceLoadComplete) {
      const timeoutId = setTimeout(() => {
        console.log("Safety timeout triggered - forcing loading state to false");
        setForceLoadComplete(true);
        
        if (isFetching && reagents.length === 0) {
          console.log("Fetch taking too long - refreshing session");
          // Reset session state to ensure fresh token
          resetSessionState();
          
          supabase.auth.refreshSession().then(({ data, error }) => {
            if (error) {
              console.error("Error refreshing session:", error);
              toast.error("Error refreshing session. Please try logging out and back in.");
            } else if (data.session) {
              console.log("Session refreshed successfully");
              toast.success("Session refreshed. Trying to load reagents again...");
            }
          });
        }
      }, 10000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loadingReagents, isFetching, forceLoadComplete, reagents.length]);

  // Calculate actual loading state
  const isActuallyLoading = (loadingReagents || isFetching || authLoading || isRetrying) && !forceLoadComplete;

  return {
    forceLoadComplete,
    isActuallyLoading
  };
};
